import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, CheckCircleIcon, PlayCircleIcon, LockIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, DownloadIcon, BookOpenIcon, ClipboardListIcon, TrophyIcon, NotebookIcon } from 'lucide-react';
import Sidebar from '../Sidebar';
import Topbar from '../Topbar';
import { CourseData, Lesson, Module } from '../../data/coursesData';
interface CourseDetailTemplateProps {
  courseData: CourseData;
}
const CourseDetailTemplate: React.FC<CourseDetailTemplateProps> = ({
  courseData
}) => {
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState(courseData.defaultLesson.moduleId);
  const [activeLesson, setActiveLesson] = useState(courseData.defaultLesson.lessonId);
  const [activeTab, setActiveTab] = useState('descrizione');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedModules, setExpandedModules] = useState<number[]>([courseData.defaultLesson.moduleId]);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modules, setModules] = useState(courseData.modules);
  const toggleModule = (moduleId: number) => {
    if (expandedModules.includes(moduleId)) {
      setExpandedModules(expandedModules.filter(id => id !== moduleId));
    } else {
      setExpandedModules([...expandedModules, moduleId]);
    }
  };
  const handleLessonClick = (moduleId: number, lessonId: number, status: string) => {
    if (status !== 'locked') {
      setActiveModule(moduleId);
      setActiveLesson(lessonId);
    }
  };
  const handleMarkAsCompleted = () => {
    // Show skeleton loading
    setIsLoading(true);
    // Simulate loading delay
    setTimeout(() => {
      // Update modules state
      const updatedModules = modules.map(module => {
        if (module.id === activeModule) {
          const updatedLessons = module.lessons.map(lesson => {
            if (lesson.id === activeLesson) {
              // Mark current lesson as completed
              return {
                ...lesson,
                status: 'completed' as const
              };
            } else if (lesson.id === activeLesson + 1) {
              // Mark next lesson as current and unlock it
              return {
                ...lesson,
                status: 'current' as const
              };
            }
            return lesson;
          });
          // Update module completed count
          const completedCount = updatedLessons.filter(l => l.status === 'completed').length;
          return {
            ...module,
            lessons: updatedLessons,
            completed: completedCount
          };
        }
        return module;
      });
      setModules(updatedModules);
      // Find next lesson
      const currentModuleData = updatedModules.find(m => m.id === activeModule);
      const nextLesson = currentModuleData?.lessons.find(l => l.id === activeLesson + 1);
      if (nextLesson) {
        // Move to next lesson
        setActiveLesson(nextLesson.id);
      }
      setIsLoading(false);
    }, 800);
  };
  const getActiveLessonData = () => {
    const module = modules.find(m => m.id === activeModule);
    const lesson = module?.lessons.find(l => l.id === activeLesson);
    return {
      module,
      lesson
    };
  };
  const getLessonIcon = (type: string, size: number = 18) => {
    switch (type) {
      case 'video':
        return <PlayCircleIcon size={size} data-id="element-951" />;
      case 'reading':
        return <BookOpenIcon size={size} data-id="element-952" />;
      case 'quiz':
        return <ClipboardListIcon size={size} data-id="element-953" />;
      case 'exercise':
        return <NotebookIcon size={size} data-id="element-954" />;
      case 'checklist':
        return <ClipboardListIcon size={size} data-id="element-955" />;
      case 'challenge':
        return <TrophyIcon size={size} data-id="element-956" />;
      case 'diary':
        return <NotebookIcon size={size} data-id="element-957" />;
      default:
        return <FileTextIcon size={size} data-id="element-958" />;
    }
  };
  const {
    module: activeModuleData,
    lesson: activeLessonData
  } = getActiveLessonData();
  // Skeleton Loading Component
  if (isLoading) {
    return <div className="flex h-screen bg-gray-50" data-id="element-959">
        <div className="hidden md:flex md:flex-shrink-0" data-id="element-960">
          <Sidebar activePage="formazione" data-id="element-961" />
        </div>

        <div className="flex flex-col flex-1 overflow-hidden" data-id="element-962">
          <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-963" />

          <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-50" data-id="element-964">
            <div className="max-w-7xl mx-auto pt-4 md:pt-6" data-id="element-965">
              <div className="mb-6" data-id="element-966">
                <div className="h-8 bg-gray-200 rounded w-2/3 mb-2 animate-pulse" data-id="element-967"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" data-id="element-968"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-969">
                <div className="lg:col-span-1" data-id="element-970">
                  <div className="bg-white rounded-xl border border-gray-100 p-4" data-id="element-971">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4 animate-pulse" data-id="element-972"></div>
                    <div className="space-y-3" data-id="element-973">
                      <div className="h-16 bg-gray-200 rounded animate-pulse" data-id="element-974"></div>
                      <div className="h-16 bg-gray-200 rounded animate-pulse" data-id="element-975"></div>
                      <div className="h-16 bg-gray-200 rounded animate-pulse" data-id="element-976"></div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2" data-id="element-977">
                  <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6" data-id="element-978">
                    <div className="w-full aspect-video bg-gray-200 animate-pulse" data-id="element-979"></div>
                    <div className="p-4" data-id="element-980">
                      <div className="h-6 bg-gray-200 rounded w-3/4 mb-2 animate-pulse" data-id="element-981"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" data-id="element-982"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>;
  }
  return <div className="flex h-screen bg-gray-50" data-id="element-983">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} data-id="element-984">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-985"></div>
        <div className="relative flex-1 flex flex-col w-2/3 h-full bg-black overflow-hidden" data-id="element-986">
          <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-987">
            <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-988">
              <XIcon className="h-6 w-6 text-white" data-id="element-989" />
            </button>
          </div>
          <div className="flex-1 h-full overflow-y-auto" data-id="element-990">
            <Sidebar activePage="formazione" data-id="element-991" />
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-992">
        <Sidebar activePage="formazione" data-id="element-993" />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-994">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-995" />

        <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-50" data-id="element-996">
          <div className="max-w-7xl mx-auto pt-4 md:pt-6" data-id="element-997">
            {/* Course title and info */}
            <div className="mb-6" data-id="element-998">
              <h1 className="text-2xl font-normal text-gray-900" data-id="element-999">
                {courseData.title}
              </h1>
              <p className="text-sm text-gray-600 font-normal mt-1" data-id="element-1000">
                {courseData.subtitle}
              </p>
              <div className="mt-3 flex items-center gap-4" data-id="element-1001">
                <span className="text-sm text-gray-700 font-normal" data-id="element-1002">
                  {courseData.progressText}
                </span>
                <div className="flex-1 max-w-xs bg-gray-200 rounded-full h-2" data-id="element-1003">
                  <div className="bg-green-600 h-2 rounded-full transition-all duration-500" style={{
                  width: `${courseData.modulesCompleted / courseData.totalModules * 100}%`
                }} data-id="element-1004"></div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1005">
              {/* Course Sidebar */}
              <div className="lg:col-span-1" data-id="element-1006">
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-id="element-1007">
                  <div className="p-4 border-b border-gray-100" data-id="element-1008">
                    <h2 className="text-base font-normal text-gray-900" data-id="element-1009">
                      Contenuti del corso
                    </h2>
                  </div>
                  <div className="py-2 max-h-[600px] overflow-y-auto" data-id="element-1010">
                    {modules.map(module => <div key={module.id} className="mb-2" data-id="element-1011">
                        <div className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${activeModule === module.id && !expandedModules.includes(module.id) ? 'bg-gray-50' : ''}`} onClick={() => toggleModule(module.id)} data-id="element-1012">
                          <div className="flex-1" data-id="element-1013">
                            <div className="flex items-center" data-id="element-1014">
                              <span className="text-sm font-normal text-gray-900" data-id="element-1015">
                                {module.title}
                              </span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500 font-normal mt-1" data-id="element-1016">
                              <span data-id="element-1017">
                                {module.completed}/{module.total} completate
                              </span>
                              <div className="w-full max-w-[100px] bg-gray-200 rounded-full h-1.5 ml-2" data-id="element-1018">
                                <div className="bg-green-600 h-1.5 rounded-full transition-all" style={{
                              width: `${module.completed / module.total * 100}%`
                            }} data-id="element-1019"></div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center" data-id="element-1020">
                            {expandedModules.includes(module.id) ? <ChevronUpIcon size={18} className="text-gray-400" strokeWidth={1.5} data-id="element-1021" /> : <ChevronDownIcon size={18} className="text-gray-400" strokeWidth={1.5} data-id="element-1022" />}
                          </div>
                        </div>
                        {expandedModules.includes(module.id) && <div className="mt-1 mb-2" data-id="element-1023">
                            {module.lessons.map(lesson => <div key={lesson.id} className={`px-6 py-2.5 flex items-center cursor-pointer transition-colors ${activeModule === module.id && activeLesson === lesson.id ? 'bg-gray-50' : 'hover:bg-gray-50'} ${lesson.status === 'locked' ? 'opacity-60' : ''}`} onClick={() => handleLessonClick(module.id, lesson.id, lesson.status)} data-id="element-1024">
                                <div className="mr-3 text-gray-500" data-id="element-1025">
                                  {lesson.status === 'completed' && <CheckCircleIcon size={18} className="text-green-600" strokeWidth={1.5} data-id="element-1026" />}
                                  {lesson.status === 'current' && getLessonIcon(lesson.type, 18)}
                                  {lesson.status === 'locked' && <LockIcon size={18} className="text-gray-400" strokeWidth={1.5} data-id="element-1027" />}
                                </div>
                                <div className="flex-1" data-id="element-1028">
                                  <div className="text-sm text-gray-900 font-normal" data-id="element-1029">
                                    {lesson.title}
                                  </div>
                                  <div className="text-xs text-gray-500 font-normal" data-id="element-1030">
                                    {lesson.duration}
                                  </div>
                                </div>
                              </div>)}
                          </div>}
                      </div>)}
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-2" data-id="element-1031">
                {/* Video/Content Player */}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden mb-6" data-id="element-1032">
                  {activeLessonData?.type === 'video' ? <>
                      <div className="relative w-full aspect-video bg-gray-900" data-id="element-1033">
                        <img src={courseData.defaultLesson.videoPlaceholder || 'https://studentclimates.wordpress.com/wp-content/uploads/2018/04/art-birte-2.png?w=825'} alt={activeLessonData.title} className="w-full h-full object-cover opacity-60" data-id="element-1034" />
                        <div className="absolute inset-0 flex items-center justify-center" data-id="element-1035">
                          <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all" data-id="element-1036">
                            <PlayCircleIcon size={32} className="text-white" strokeWidth={1.5} data-id="element-1037" />
                          </div>
                        </div>
                      </div>
                      <div className="p-4 border-b border-gray-100" data-id="element-1038">
                        <div className="flex items-center justify-between" data-id="element-1039">
                          <div data-id="element-1040">
                            <h2 className="text-lg font-normal text-gray-900" data-id="element-1041">
                              {activeLessonData?.title}
                            </h2>
                            <p className="text-sm text-gray-500 font-normal" data-id="element-1042">
                              Modulo {activeModule}: {activeModuleData?.title}
                            </p>
                          </div>
                          <button onClick={handleMarkAsCompleted} className="px-4 py-2 bg-green-600 text-white text-sm font-normal rounded-full hover:bg-green-700 transition-colors" data-id="element-1043">
                            Segna come completata
                          </button>
                        </div>
                      </div>
                    </> : <>
                      <div className="p-4 border-b border-gray-100" data-id="element-1044">
                        <div className="flex items-center justify-between" data-id="element-1045">
                          <div data-id="element-1046">
                            <h2 className="text-lg font-normal text-gray-900" data-id="element-1047">
                              {activeLessonData?.title}
                            </h2>
                            <p className="text-sm text-gray-500 font-normal" data-id="element-1048">
                              Modulo {activeModule}: {activeModuleData?.title}
                            </p>
                          </div>
                          <button onClick={handleMarkAsCompleted} className="px-4 py-2 bg-green-600 text-white text-sm font-normal rounded-full hover:bg-green-700 transition-colors" data-id="element-1049">
                            Segna come completata
                          </button>
                        </div>
                      </div>
                      {courseData.defaultLesson.readingContent ? <div className="p-6 max-h-[600px] overflow-y-auto" data-id="element-1050">
                          <div className="prose prose-sm max-w-none
                            prose-headings:font-normal prose-headings:text-gray-900
                            prose-h2:text-xl prose-h2:mb-4 prose-h2:mt-6
                            prose-h3:text-base prose-h3:mb-3 prose-h3:mt-5
                            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4 prose-p:font-normal
                            prose-ul:text-gray-700 prose-ul:font-normal prose-ul:leading-relaxed
                            prose-ol:text-gray-700 prose-ol:font-normal prose-ol:leading-relaxed
                            prose-li:mb-2
                            prose-strong:font-semibold" dangerouslySetInnerHTML={{
                      __html: courseData.defaultLesson.readingContent
                    }} data-id="element-1051" />
                        </div> : <div className="w-full aspect-video flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200" data-id="element-1052">
                          <div className="text-center" data-id="element-1053">
                            {getLessonIcon(activeLessonData?.type || 'reading', 48)}
                            <p className="text-sm text-gray-600 font-normal mt-2" data-id="element-1054">
                              Contenuto di lettura
                            </p>
                          </div>
                        </div>}
                    </>}
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-id="element-1055">
                  <div className="flex border-b border-gray-100" data-id="element-1056">
                    <button className={`px-4 py-3 text-sm font-normal flex-1 text-center transition-colors ${activeTab === 'descrizione' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('descrizione')} data-id="element-1057">
                      Descrizione
                    </button>
                    <button className={`px-4 py-3 text-sm font-normal flex-1 text-center transition-colors ${activeTab === 'materiali' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('materiali')} data-id="element-1058">
                      Materiali
                    </button>
                    <button className={`px-4 py-3 text-sm font-normal flex-1 text-center transition-colors ${activeTab === 'note' ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setActiveTab('note')} data-id="element-1059">
                      Note
                    </button>
                  </div>

                  <div className="p-5" data-id="element-1060">
                    {activeTab === 'descrizione' && <div className="space-y-4" data-id="element-1061">
                        <h3 className="text-base font-normal text-gray-900" data-id="element-1062">
                          Obiettivi di apprendimento
                        </h3>
                        <ul className="list-disc pl-5 text-sm text-gray-600 font-normal space-y-2 leading-relaxed" data-id="element-1063">
                          {courseData.defaultLesson.objectives.map((obj, index) => <li key={index} data-id="element-1064">{obj}</li>)}
                        </ul>
                        <h3 className="text-base font-normal text-gray-900 pt-2" data-id="element-1065">
                          Descrizione
                        </h3>
                        <p className="text-sm text-gray-600 font-normal leading-relaxed" data-id="element-1066">
                          {courseData.defaultLesson.description}
                        </p>
                      </div>}

                    {activeTab === 'materiali' && <div className="space-y-3" data-id="element-1067">
                        {courseData.defaultLesson.materials.length > 0 ? courseData.defaultLesson.materials.map((material, index) => <div key={index} className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-1068">
                                <div className="mr-3 text-gray-500" data-id="element-1069">
                                  <FileTextIcon size={20} strokeWidth={1.5} data-id="element-1070" />
                                </div>
                                <div className="flex-1" data-id="element-1071">
                                  <div className="text-sm font-normal text-gray-900" data-id="element-1072">
                                    {material.title}
                                  </div>
                                  <div className="text-xs text-gray-500 font-normal" data-id="element-1073">
                                    {material.type}
                                  </div>
                                </div>
                                <button className="p-2 text-gray-500 hover:text-green-600 transition-colors" data-id="element-1074">
                                  <DownloadIcon size={18} strokeWidth={1.5} data-id="element-1075" />
                                </button>
                              </div>) : <div className="text-center py-8 text-sm text-gray-500 font-normal" data-id="element-1076">
                            Nessun materiale aggiuntivo.
                          </div>}
                      </div>}

                    {activeTab === 'note' && <div data-id="element-1077">
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Scrivi una nota…" className="w-full p-3 border border-gray-200 rounded-lg text-sm font-normal focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent min-h-[200px] resize-none" data-id="element-1078" />
                        <div className="flex justify-end mt-3" data-id="element-1079">
                          <button className="px-4 py-2 bg-green-600 text-white text-sm font-normal rounded-lg hover:bg-green-700 transition-colors" data-id="element-1080">
                            Salva note
                          </button>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>;
};
export default CourseDetailTemplate;